import { Component, inject, Input } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { firstValueFrom } from 'rxjs';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { ParcsDataService } from 'src/app/core/services/data/parcs-data.service';
import { FlashAccountByLotResponse, IFlashCertificationData, IFlashLocationProfile, IFlashMonthlyAccount, IFlashPool, IFlashVehicleLinkResult } from 'src/app/shared/code/interfaces/parcs-interfaces';
import { IParkerProfileDetail } from 'src/app/shared/interfaces';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatListModule } from '@angular/material/list';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  DialogFlashAddNewAccountComponent,
  IFlashAddNewAccountRequest,
} from 'src/app/core/dialog/dialog-flash-add-new-account/dialog-flash-add-new-account.component';

@Component({
  selector: 'app-flash-parker-settings',
  standalone: true,
  imports: [
     CommonModule,
        MatFormFieldModule,
        MatSelectModule,
        FormsModule,
        MatOptionModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatListModule,
        MatCheckboxModule,
        MatCardModule,
        MatSlideToggleModule,
  ],
  templateUrl: './flash-parker-settings.component.html',
  styleUrl: './flash-parker-settings.component.scss'
})
export class FlashParkerSettingsComponent {
  _parkerDetail: IParkerProfileDetail;
  @Input() set parkerDetail(value: IParkerProfileDetail) {
    this._parkerDetail = value;
    this.initSettingsData();
  }

  hasAccessHolder: boolean = false;
  flashAccount: IFlashMonthlyAccount | null = null;
  allowPassback: boolean = false;
  // Starts true so the first render shows the checking state instead of flashing the "not linked" view.
  isLookupInProgress: boolean = true;
  isResetToNeutralInProgress: boolean = false;
  isAddAccountInProgress: boolean = false;

  // TEMPORARY test harness for SetMonthlyAccountExternalID - remove once the Flash sync feature lands.
  // The account number is a hardcoded Flash sandbox account created by hand in the Flash Portal.
  readonly testFlashAccountNumber: string = '25087693';
  isConvertExternalAccountInProgress: boolean = false;

  // TEMPORARY Flash certification evidence - remove with the PARCS tab block once screenshots are taken.
  certificationData: IFlashCertificationData | null = null;
  isCertificationLoadInProgress: boolean = false;

  // ==================== Flash Profile / Pool ====================
  profileList: IFlashLocationProfile[] = [];
  selectedProfileId: string = '';
  selectedPoolName: string = '';
  // Flash has no "list pools" endpoint, so the names come from the lot's spACE-AR pool admin list.
  poolOptions: string[] = [];
  isProfileUpdateInProgress: boolean = false;

  //Services
   _appGeneralState = inject(AppGeneralStateService);
   _parcsDataService = inject(ParcsDataService);
   _messageDialogService = inject(MessageDialogService);
   _matDialogService = inject(MatDialog);
   _matSnackBar = inject(MatSnackBar);

  constructor() {
    // undefined drives the "Getting PARCS Status..." tab header until the lookup resolves.
    this._appGeneralState.hasLinkedParcsAccount = undefined;
  }

  async initSettingsData(): Promise<void> {
    this._appGeneralState.hasLinkedParcsAccount = undefined;

    this.hasAccessHolder = await this.getFlashAccountData(
      this._parkerDetail.parkerId,
      this._appGeneralState.currentLotNumber,
    );

    if (this.hasAccessHolder) {
      // Both lists have to land before the selections are matched back onto the dropdowns.
      await Promise.all([this.getProfileList(), this.getPoolList()]);
      this.syncSelectionsToAccount();
    }
  }

  /// Clears the parker's passback position in Flash so they can enter again.
  /// Flash answers HTTP 200 even on failure, so isSuccess decides, not the status code.
  async onFlashResetToNeutral(): Promise<void> {
    if (this.isResetToNeutralInProgress) {
      return;
    }
    this.isResetToNeutralInProgress = true;

    try {
      const response = await firstValueFrom(
        this._parcsDataService.flash_ResetAccountToNeutral(
          this._appGeneralState.currentLotNumber,
          this._parkerDetail.parkerId,
        ),
      );

      if (response.isSuccess) {
        this._messageDialogService.snackBarTopCenterTimed('This Parker has been reset to neutral in Flash.', 5000);
        await this.getFlashAccountData(this._parkerDetail.parkerId, this._appGeneralState.currentLotNumber);
      } else {
        this._messageDialogService.displayGenericErrorDialog(
          'Flash System Error',
          'The Parker could not be reset to neutral.',
          response.message || 'Flash did not return a reason.  Please try again.',
        );
      }
    } catch (err) {
      this._messageDialogService.displayGenericErrorDialog(
        'Flash System Error',
        'The Parker could not be reset to neutral.',
        'An unknown error occurred.  Please try again.',
      );
    }

    this.isResetToNeutralInProgress = false;
  }

  /// Looks the parker up in Flash by ParkerId as the External account number.
  /// Flash answers HTTP 200 even when the account does not exist, so isSuccess decides, not the status code.
  async getFlashAccountData(parkerId: string, lotNumber: string): Promise<boolean> {
    let hasAccount: boolean = false;
    this.isLookupInProgress = true;

    try {
      const response: FlashAccountByLotResponse = await firstValueFrom(
        this._parcsDataService.flash_GetAccountByLotParkerId(lotNumber, parkerId),
      );

      hasAccount = response.isSuccess;
      this.flashAccount = hasAccount ? response.account : null;
      this.allowPassback = this.flashAccount?.allowPassback ?? false;
    } catch (err) {
      hasAccount = false;
      this.flashAccount = null;
      this.allowPassback = false;
    }

    this.isLookupInProgress = false;
    this._appGeneralState.hasLinkedParcsAccount = hasAccount;

    return hasAccount;
  }

  onAllowPassbackChange(allowPassback: boolean): void {
    if (allowPassback === this.allowPassback) {
      return;
    }

    const previousAllowPassback = this.allowPassback;
    this.allowPassback = allowPassback;
    this.updateFlashAccountSettings(
      `Allow Passback is now ${allowPassback ? 'ON' : 'OFF'} for this Parker in Flash.`,
      'Allow Passback could not be updated.',
      this.selectedProfileId,
      this.selectedPoolName,
      previousAllowPassback,
    );
  }

  /// Loads the lot's Flash profiles so the dropdown can offer them by name.
  /// Flash answers HTTP 200 even on failure, so isSuccess decides, not the status code.
  async getProfileList(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this._parcsDataService.flash_GetProfilesByLotNumber(this._appGeneralState.currentLotNumber),
      );

      this.profileList = response.isSuccess ? response.locationProfiles : [];
    } catch (err) {
      this.profileList = [];
    }
  }

  async getPoolList(): Promise<void> {
    try {
      const pools: IFlashPool[] = await firstValueFrom(
        this._parcsDataService.flash_GetPoolsByLotNumber(this._appGeneralState.currentLotNumber),
      );

      this.poolOptions = pools.map((pool) => pool.poolName);
    } catch (err) {
      this.poolOptions = [];
    }
  }

  // Flash's built-in catch-all profile. GetMonthlyProfiles always includes it (lanai-api's
  // Flash_Http synthesizes the entry when the location has not configured one itself), so it is
  // always a valid fallback option on the dropdown.
  private readonly FLASH_ALL_ACCESS_PROFILE_NAME = 'All Access';

  /// Matches the account's current profile/pool back onto the dropdowns.
  /// Flash reports the profile by NAME while the dropdown is keyed on uniqueID, and the account can
  /// hold a pool configured outside this list, so an unrecognized value is added as an option rather
  /// than silently showing the field as empty.
  private syncSelectionsToAccount(): void {
    const currentProfileName = (this.flashAccount?.profile ?? '').trim();
    const matchedProfile = this.profileList.find(
      (profile) => profile.name.toLowerCase() === currentProfileName.toLowerCase(),
    );

    // Flash treats a blank/"No Profile" account as using its "All Access" catch-all, so default the
    // dropdown there instead of the blank placeholder whenever the account's profile name does not
    // match one of the lot's real profiles.
    const allAccessProfile = this.profileList.find(
      (profile) => profile.name.toLowerCase() === this.FLASH_ALL_ACCESS_PROFILE_NAME.toLowerCase(),
    );

    this.selectedProfileId = matchedProfile?.uniqueID ?? allAccessProfile?.uniqueID ?? '';

    const currentPoolName = (this.flashAccount?.poolName ?? '').trim();
    this.selectedPoolName = currentPoolName;

    if (currentPoolName !== '' && !this.poolOptions.includes(currentPoolName)) {
      this.poolOptions = [currentPoolName, ...this.poolOptions];
    }
  }

  /// Flash has no concept of clearing a profile, so only a move to a different, non-blank value writes.
  onProfileChange(profileId: string): void {
    if (profileId === '' || profileId === this.selectedProfileId) {
      return;
    }

    const previousProfileId = this.selectedProfileId;
    this.selectedProfileId = profileId;
    this.updateFlashAccountSettings(
      'The Flash Profile has been updated for this Parker.',
      'The Flash Profile could not be updated.',
      previousProfileId,
      this.selectedPoolName,
      this.allowPassback,
    );
  }

  /// Flash has no concept of clearing a pool, so only a move to a different, non-blank value writes.
  onPoolChange(poolName: string): void {
    if (poolName === '' || poolName === this.selectedPoolName) {
      return;
    }

    const previousPoolName = this.selectedPoolName;
    this.selectedPoolName = poolName;
    this.updateFlashAccountSettings(
      'The Flash Pool has been updated for this Parker.',
      'The Flash Pool could not be updated.',
      this.selectedProfileId,
      previousPoolName,
      this.allowPassback,
    );
  }

  /// Pushes the current profile, pool and passback selections to Flash.
  /// All three are always sent because MonthlyAccount5 replaces the whole record; a blank value is
  /// ignored by Flash on an update, so an unmatched profile or pool leaves the Flash-side value intact.
  /// Flash answers HTTP 200 even on failure, so isSuccess decides, not the status code.
  private async updateFlashAccountSettings(
    successMessage: string,
    failureMessage: string,
    previousProfileId: string,
    previousPoolName: string,
    previousAllowPassback: boolean,
  ): Promise<void> {
    this.isProfileUpdateInProgress = true;

    try {
      const response = await firstValueFrom(
        this._parcsDataService.flash_UpdateAccountProfile(
          this._appGeneralState.currentLotNumber,
          this._parkerDetail.parkerId,
          {
            // Flash resolves the profile by NAME, not by the uniqueID the dropdown is keyed on.
            profileName: this.profileList.find((profile) => profile.uniqueID === this.selectedProfileId)?.name ?? '',
            poolName: this.selectedPoolName,
            allowPassback: this.allowPassback,
          },
        ),
      );

      if (response.isSuccess) {
        this._messageDialogService.snackBarTopCenterTimed(successMessage, 5000);
        // Refreshes the displayed account and the passback toggle; the dropdowns keep the user's choice
        // because Flash echoes portal display text that does not always match a profile name in the list.
        await this.getFlashAccountData(this._parkerDetail.parkerId, this._appGeneralState.currentLotNumber);
      } else {
        this.selectedProfileId = previousProfileId;
        this.selectedPoolName = previousPoolName;
        this.allowPassback = previousAllowPassback;
        this._messageDialogService.displayGenericErrorDialog(
          'Flash System Error',
          failureMessage,
          response.message || 'Flash did not return a reason.  Please try again.',
        );
      }
    } catch (err) {
      this.selectedProfileId = previousProfileId;
      this.selectedPoolName = previousPoolName;
      this.allowPassback = previousAllowPassback;
      this._messageDialogService.displayGenericErrorDialog(
        'Flash System Error',
        failureMessage,
        'An unknown error occurred.  Please try again.',
      );
    }

    this.isProfileUpdateInProgress = false;
  }

  /// Creates a Flash account for a Parker that exists in spACE-AR but was never linked to Flash.
  /// Flash answers HTTP 200 even on failure, so isSuccess decides, not the status code.
  /// TEMPORARY. Attaches this parker's ParkerId to the hardcoded Flash account as its external
  /// account number, so a portal-created account becomes addressable by the Monthly APIs.
  /// Flash answers HTTP 200 even on failure, so isSuccess decides, not the status code.
  async onConvertExternalAccount(): Promise<void> {
    if (this.isConvertExternalAccountInProgress) {
      return;
    }
    this.isConvertExternalAccountInProgress = true;

    try {
      const response = await firstValueFrom(
        this._parcsDataService.flash_LinkPortalAccountToParker(
          this._appGeneralState.currentLotNumber,
          this.testFlashAccountNumber,
          this._parkerDetail.parkerId,
        ),
      );

      const detail: string =
        `isSuccess: ${response.isSuccess} | code: ${response.code} | http: ${response.httpStatusCode}` +
        ` | flashAccountNumber: ${response.flashAccountNumber || '--'}` +
        ` | accountGuid: ${response.accountGuid || '--'}` +
        ` | previousExternalId: ${response.previousExternalId || '(none)'}` +
        ` | parkerId: ${response.parkerId}` +
        ` | message: ${response.message || '(none)'}` +
        ` || vehicles linked: ${response.vehiclesLinked ?? 0} of ${response.vehiclesEvaluated ?? 0}` +
        this.describeVehicleLinkResults(response.vehicles);

      // No duration - the result is the point of the test, so it stays until dismissed.
      this._matSnackBar.open(detail, 'Close', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });

      if (response.isSuccess) {
        // Re-running the lookup is the real proof: the account can now be found by ParkerId.
        await this.initSettingsData();
      }
    } catch (err: any) {
      this._matSnackBar.open(
        `Convert failed before reaching Flash: ${err?.message ?? 'unknown error'}`,
        'Close',
        { horizontalPosition: 'center', verticalPosition: 'top' },
      );
    }

    this.isConvertExternalAccountInProgress = false;
  }

  /// TEMPORARY. Flattens the per-vehicle match outcomes into the test snackbar, so a plate that
  /// failed to match is visible without reading the network tab.
  private describeVehicleLinkResults(vehicles: IFlashVehicleLinkResult[] | undefined): string {
    if (!vehicles?.length) {
      return '';
    }

    return vehicles
      .map(
        (vehicle) =>
          ` || ${vehicle.flashLicensePlate || '(no plate)'} -> ` +
          `${vehicle.isSuccess ? `vehicleId ${vehicle.vehicleId}` : vehicle.code}`,
      )
      .join('');
  }

  /// TEMPORARY. Pulls GetMonthlyAccount1, GetMonthlyContact1 and GetMonthlyVehicle1 for the Flash
  /// certification test cases. Remove with the PARCS tab block once screenshots are taken.
  async onLoadCertificationData(): Promise<void> {
    if (this.isCertificationLoadInProgress) {
      return;
    }
    this.isCertificationLoadInProgress = true;

    try {
      this.certificationData = await firstValueFrom(
        this._parcsDataService.flash_GetCertificationData(
          this._appGeneralState.currentLotNumber,
          this._parkerDetail.parkerId,
        ),
      );

      if (!this.certificationData.isSuccess) {
        this._matSnackBar.open(
          `Flash did not return the account: ${this.certificationData.code} - ${this.certificationData.message || '(no reason given)'}`,
          'Close',
          { horizontalPosition: 'center', verticalPosition: 'top' },
        );
      }
    } catch (err: any) {
      this.certificationData = null;
      this._matSnackBar.open(`Pull failed before reaching Flash: ${err?.message ?? 'unknown error'}`, 'Close', {
        horizontalPosition: 'center',
        verticalPosition: 'top',
      });
    }

    this.isCertificationLoadInProgress = false;
  }

  /// TEMPORARY. Renders whatever Flash returned rather than a fixed field list, so nothing is hidden
  /// from the certification screenshot. Objects and arrays are JSON so nested values stay visible.
  toDisplayFields(payload: Record<string, any> | null | undefined): { key: string; value: string }[] {
    if (!payload) {
      return [];
    }

    return Object.keys(payload).map((key) => {
      const rawValue: any = payload[key];
      let value: string;

      if (rawValue === null || rawValue === undefined || rawValue === '') {
        value = '--';
      } else if (typeof rawValue === 'object') {
        value = JSON.stringify(rawValue);
      } else {
        value = String(rawValue);
      }

      return { key, value };
    });
  }

  onAddParkerToFlash(): void {
    const dialogRef = this._matDialogService.open(DialogFlashAddNewAccountComponent, {
      data: {
        lotNumber: this._appGeneralState.currentLotNumber,
        parkerId: this._parkerDetail.parkerId,
        cardNumber: this._parkerDetail.cardNumber,
      },
      hasBackdrop: true,
      width: '800px',
      maxWidth: '800px',
      height: '480px',
      maxHeight: '480px',
      disableClose: false,
      panelClass: 'custom-dialog-container',
      position: { top: '150px' },
    });

    dialogRef.afterClosed().subscribe(async (result: IFlashAddNewAccountRequest | false | undefined) => {
      if (!result) {
        return;
      }

      this.isAddAccountInProgress = true;

      try {
        const response = await firstValueFrom(
          this._parcsDataService.flash_AddAccountForExistingParker(
            this._appGeneralState.currentLotNumber,
            this._parkerDetail.parkerId,
            {
              profileName: result.profileName,
              poolName: result.poolName,
            },
          ),
        );

        if (response.isSuccess) {
          // The account is created even when a contact or vehicle did not make it across.
          this._messageDialogService.snackBarTopCenterTimed(
            response.warningMessage
              ? `This Parker has been added to Flash. ${response.warningMessage}`
              : 'This Parker has been successfully added to Flash.',
            5000,
          );
          await this.initSettingsData();
        } else {
          this._messageDialogService.displayGenericErrorDialog(
            'Flash System Error',
            'This Parker could not be added to Flash.',
            response.message || 'Flash did not return a reason.  Please try again.',
          );
        }
      } catch (err) {
        this._messageDialogService.displayGenericErrorDialog(
          'Flash System Error',
          'This Parker could not be added to Flash.',
          'An unknown error occurred.  Please try again.',
        );
      }

      this.isAddAccountInProgress = false;
    });
  }
}
  